import { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { IconClose } from './Icons'
import { useModalA11y } from '../utils/useModalA11y'
import {
  roughRect,
  roughDiamondInRect,
  roughEllipseInRect,
  roughArrow,
  nodeCenter,
  nodeHalfDiag,
  shortenSegment,
  findNodeAtPoint,
  getNodeResizeHandles,
  hitResizeHandle,
  RESIZE_HANDLE_RADIUS,
} from '../utils/roughFlowDraw'
import './FlowEditorModal.css'

const SIZES = [
  { w: 800, h: 560, label: '800×560' },
  { w: 900, h: 640, label: '900×640' },
  { w: 1024, h: 720, label: '1024×720' },
]

const SHAPE_DEFAULTS = {
  rect: { w: 168, h: 72 },
  diamond: { w: 128, h: 112 },
  ellipse: { w: 148, h: 76 },
}

const MIN_NODE_SIZE = 40

function newNode(type, cx, cy) {
  const def = SHAPE_DEFAULTS[type]
  const text =
    type === 'ellipse' ? 'התחלה' : type === 'diamond' ? 'החלטה?' : 'צעד'
  return {
    id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type,
    x: cx - def.w / 2,
    y: cy - def.h / 2,
    w: def.w,
    h: def.h,
    text,
    seed: (Math.random() * 0xffffffff) >>> 0,
  }
}

function edgeSeed(from, to) {
  const s = `${from}>${to}`
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619) >>> 0
  }
  return h >>> 0
}

function drawResizeHandles(ctx, n, accent) {
  const handles = getNodeResizeHandles(n)
  ctx.save()
  ctx.fillStyle = accent || '#2563eb'
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 2
  for (const h of handles) {
    ctx.beginPath()
    ctx.arc(h.x, h.y, RESIZE_HANDLE_RADIUS * 0.55, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
  }
  ctx.restore()
}

function drawFlow(ctx, nodes, edges, { selectedId, connectFromId, accent }) {
  const { width: cw, height: ch } = ctx.canvas
  ctx.save()
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, cw, ch)

  for (const e of edges) {
    const a = nodes.find((n) => n.id === e.from)
    const b = nodes.find((n) => n.id === e.to)
    if (!a || !b) continue
    const c1 = nodeCenter(a)
    const c2 = nodeCenter(b)
    const d1 = nodeHalfDiag(a) * 0.72
    const d2 = nodeHalfDiag(b) * 0.72
    const s = shortenSegment(c1.x, c1.y, c2.x, c2.y, d1, d2)
    roughArrow(ctx, s.x1, s.y1, s.x2, s.y2, e.seed ?? edgeSeed(e.from, e.to), 2.05)
  }

  for (const n of nodes) {
    const seed = n.seed ?? 1
    if (n.type === 'rect') roughRect(ctx, n.x, n.y, n.w, n.h, seed, 2.05)
    else if (n.type === 'diamond') roughDiamondInRect(ctx, n.x, n.y, n.w, n.h, seed, 2.05)
    else roughEllipseInRect(ctx, n.x, n.y, n.w, n.h, seed, 2.05)

    ctx.save()
    ctx.fillStyle = '#141414'
    ctx.font = '600 14px Heebo, system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const c = nodeCenter(n)
    const raw = (n.text || '').trim() || '…'
    const lines = raw.split(/\n/).slice(0, 4)
    const lineH = 17
    const startY = c.y - ((lines.length - 1) * lineH) / 2
    lines.forEach((line, i) => {
      ctx.fillText(line.slice(0, 42), c.x, startY + i * lineH)
    })
    ctx.restore()
  }

  if (selectedId) {
    const n = nodes.find((x) => x.id === selectedId)
    if (n) {
      const pad = 6
      ctx.save()
      ctx.strokeStyle = accent || '#2563eb'
      ctx.lineWidth = 2
      ctx.setLineDash([6, 4])
      ctx.strokeRect(n.x - pad, n.y - pad, n.w + pad * 2, n.h + pad * 2)
      ctx.restore()
      drawResizeHandles(ctx, n, accent)
    }
  }

  if (connectFromId) {
    const n = nodes.find((x) => x.id === connectFromId)
    if (n) {
      const c = nodeCenter(n)
      ctx.save()
      ctx.fillStyle = 'rgba(37, 99, 235, 0.25)'
      ctx.beginPath()
      ctx.arc(c.x, c.y, 10, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }
  }

  ctx.restore()
}

function resizeNodeFromHandle(node, handle, px, py, bounds) {
  const m = 8
  const right0 = node.x + node.w
  const bottom0 = node.y + node.h
  let x = node.x
  let y = node.y
  let w = node.w
  let h = node.h

  if (handle === 'se') {
    w = Math.max(MIN_NODE_SIZE, px - x)
    h = Math.max(MIN_NODE_SIZE, py - y)
  } else if (handle === 'sw') {
    w = Math.max(MIN_NODE_SIZE, right0 - px)
    h = Math.max(MIN_NODE_SIZE, py - y)
    x = right0 - w
  } else if (handle === 'ne') {
    w = Math.max(MIN_NODE_SIZE, px - x)
    h = Math.max(MIN_NODE_SIZE, bottom0 - py)
    y = bottom0 - h
  } else if (handle === 'nw') {
    w = Math.max(MIN_NODE_SIZE, right0 - px)
    h = Math.max(MIN_NODE_SIZE, bottom0 - py)
    x = right0 - w
    y = bottom0 - h
  }

  x = Math.max(m, Math.min(bounds.w - w - m, x))
  y = Math.max(m, Math.min(bounds.h - h - m, y))
  w = Math.min(w, bounds.w - x - m)
  h = Math.min(h, bounds.h - y - m)

  return { ...node, x, y, w, h }
}

export function FlowEditorModal({ onCreate, onClose, lang = 'he' }) {
  const t = (en, he) => (lang === 'en' ? en : he)
  const [sizeIdx, setSizeIdx] = useState(1)
  const size = SIZES[sizeIdx]
  const [nodes, setNodes] = useState([])
  const [edges, setEdges] = useState([])
  const [mode, setMode] = useState('select') // select | rect | diamond | ellipse | connect
  const [selectedId, setSelectedId] = useState(null)
  const [connectFrom, setConnectFrom] = useState(null)
  const canvasRef = useRef(null)
  const panelRef = useRef(null)
  const dragRef = useRef(null)

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedId) ?? null,
    [nodes, selectedId],
  )

  useModalA11y({ onClose, modalRef: panelRef })

  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    drawFlow(ctx, nodes, edges, {
      selectedId,
      connectFromId: connectFrom,
      accent: '#2563eb',
    })
  }, [nodes, edges, selectedId, connectFrom])

  useEffect(() => {
    redraw()
  }, [redraw])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = size.w
    canvas.height = size.h
    redraw()
  }, [size.w, size.h, redraw])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setConnectFrom(null)
        return
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!selectedId) return
        if (e.target.closest?.('input, textarea, select')) return
        e.preventDefault()
        setEdges((prev) => prev.filter((x) => x.from !== selectedId && x.to !== selectedId))
        setNodes((prev) => prev.filter((x) => x.id !== selectedId))
        setSelectedId(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedId])

  const canvasCoords = (e) => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const sx = canvas.width / rect.width
    const sy = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * sx,
      y: (e.clientY - rect.top) * sy,
    }
  }

  const clampNode = useCallback((n) => {
    const m = 8
    let { x, y, w, h } = n
    w = Math.max(MIN_NODE_SIZE, Math.min(size.w - m * 2, w))
    h = Math.max(MIN_NODE_SIZE, Math.min(size.h - m * 2, h))
    x = Math.max(m, Math.min(size.w - w - m, x))
    y = Math.max(m, Math.min(size.h - h - m, y))
    return { ...n, x, y, w, h }
  }, [size.w, size.h])

  const updateSelectedText = (text) => {
    if (!selectedId) return
    setNodes((prev) => prev.map((n) => (n.id === selectedId ? { ...n, text } : n)))
  }

  const duplicateSelected = () => {
    if (!selectedNode) return
    const copy = {
      ...selectedNode,
      id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      x: selectedNode.x + 24,
      y: selectedNode.y + 24,
      seed: (Math.random() * 0xffffffff) >>> 0,
    }
    const placed = clampNode(copy)
    setNodes((prev) => [...prev, placed])
    setSelectedId(placed.id)
    setMode('select')
  }

  const renderToCanvas = (exportNodes, exportEdges) => {
    const canvas = document.createElement('canvas')
    canvas.width = size.w
    canvas.height = size.h
    const ctx = canvas.getContext('2d')
    drawFlow(ctx, exportNodes, exportEdges, { selectedId: null, connectFromId: null })
    return canvas
  }

  const applyToPage = (onlySelected = false) => {
    if (onlySelected) {
      if (!selectedNode) return
      const relatedEdges = edges.filter(
        (e) => e.from === selectedId || e.to === selectedId,
      )
      onCreate?.(renderToCanvas([selectedNode], relatedEdges))
    } else {
      if (nodes.length === 0) return
      onCreate?.(renderToCanvas(nodes, edges))
    }
    onClose?.()
  }

  const onPointerDown = (e) => {
    const p = canvasCoords(e)
    if (!p) return

    if (mode === 'select' && selectedNode) {
      const handleHit = hitResizeHandle(getNodeResizeHandles(selectedNode), p.x, p.y)
      if (handleHit) {
        dragRef.current = {
          kind: 'resize',
          id: selectedNode.id,
          handle: handleHit,
        }
        return
      }
    }

    const hit = findNodeAtPoint(nodes, p.x, p.y)

    if (mode === 'connect') {
      if (!hit) return
      if (!connectFrom) {
        setConnectFrom(hit.id)
        setSelectedId(hit.id)
        return
      }
      if (connectFrom === hit.id) {
        setConnectFrom(null)
        return
      }
      const from = connectFrom
      const to = hit.id
      setEdges((prev) => {
        if (prev.some((x) => x.from === from && x.to === to)) return prev
        return [...prev, { id: `e-${from}-${to}`, from, to, seed: edgeSeed(from, to) }]
      })
      setConnectFrom(null)
      setSelectedId(hit.id)
      return
    }

    if (mode === 'select') {
      if (hit) {
        dragRef.current = { kind: 'move', id: hit.id, sx: p.x - hit.x, sy: p.y - hit.y }
        setSelectedId(hit.id)
      } else {
        setSelectedId(null)
        setConnectFrom(null)
      }
      return
    }

    if (mode === 'rect' || mode === 'diamond' || mode === 'ellipse') {
      if (hit) {
        dragRef.current = { kind: 'move', id: hit.id, sx: p.x - hit.x, sy: p.y - hit.y }
        setSelectedId(hit.id)
        setMode('select')
        return
      }
      const nn = newNode(mode, p.x, p.y)
      setNodes((prev) => [...prev, clampNode(nn)])
      setSelectedId(nn.id)
      setMode('select')
    }
  }

  const onPointerMove = (e) => {
    const d = dragRef.current
    if (!d) return
    const p = canvasCoords(e)
    if (!p) return

    if (d.kind === 'resize') {
      setNodes((prev) =>
        prev.map((n) =>
          n.id === d.id
            ? clampNode(resizeNodeFromHandle(n, d.handle, p.x, p.y, { w: size.w, h: size.h }))
            : n,
        ),
      )
      return
    }

    setNodes((prev) =>
      prev.map((n) =>
        n.id === d.id ? clampNode({ ...n, x: p.x - d.sx, y: p.y - d.sy }) : n,
      ),
    )
  }

  const onPointerUp = () => {
    dragRef.current = null
  }

  const onDoubleClick = (e) => {
    const p = canvasCoords(e)
    if (!p) return
    const hit = findNodeAtPoint(nodes, p.x, p.y)
    if (!hit) return
    const next = window.prompt(t('Node text', 'טקסט בתוך הצורה'), hit.text || '')
    if (next == null) return
    setNodes((prev) => prev.map((n) => (n.id === hit.id ? { ...n, text: next } : n)))
    setSelectedId(hit.id)
    setMode('select')
  }

  const clearAll = () => {
    setNodes([])
    setEdges([])
    setSelectedId(null)
    setConnectFrom(null)
  }

  const deleteSelected = () => {
    if (!selectedId) return
    setEdges((prev) => prev.filter((x) => x.from !== selectedId && x.to !== selectedId))
    setNodes((prev) => prev.filter((x) => x.id !== selectedId))
    setSelectedId(null)
  }

  const hintText =
    mode === 'connect'
      ? t('Tap two shapes to connect with an arrow.', 'לחצו על שני צורות ברצף כדי לחבר בחץ.')
      : mode === 'select'
        ? t(
            'Select: tap a shape, drag to move, corner handles to resize. Export one shape below.',
            'בחירה: לחיצה על צורה, גרירה להזזה, ידיות בפינות לשינוי גודל. אפשר לייצא צורה בודדת למטה.',
          )
        : t(
            'After placing a shape, selection mode turns on. Double-click to edit text.',
            'אחרי הצבת צורה עוברים לבחירה. לחיצה כפולה לעריכת טקסט.',
          )

  return (
    <div className="flow-editor-modal" role="dialog" aria-modal="true" aria-label={t('Flow diagram', 'תרשים זרימה')}>
      <div className="flow-editor-backdrop" onClick={onClose} aria-hidden />
      <div className="flow-editor-shell" ref={panelRef} tabIndex={-1}>
        <div className="flow-editor-header">
          <h2>{t('Flow sketch (Excalidraw-style)', 'תרשים זרימה — סגנון Excalidraw')}</h2>
          <button type="button" className="flow-editor-close" onClick={onClose} aria-label={t('Close', 'סגור')}>
            <IconClose size={22} />
          </button>
        </div>

        <div className="flow-editor-toolbar">
          <button type="button" className={`flow-editor-tool-btn ${mode === 'select' ? 'active' : ''}`} onClick={() => { setMode('select'); setConnectFrom(null); }}>
            {t('Select object', 'בחירת אובייקט')}
          </button>
          <button type="button" className={`flow-editor-tool-btn ${mode === 'rect' ? 'active' : ''}`} onClick={() => { setMode('rect'); setConnectFrom(null); }}>
            {t('Rectangle', 'מלבן')}
          </button>
          <button type="button" className={`flow-editor-tool-btn ${mode === 'diamond' ? 'active' : ''}`} onClick={() => { setMode('diamond'); setConnectFrom(null); }}>
            {t('Diamond', 'מעוין')}
          </button>
          <button type="button" className={`flow-editor-tool-btn ${mode === 'ellipse' ? 'active' : ''}`} onClick={() => { setMode('ellipse'); setConnectFrom(null); }}>
            {t('Ellipse', 'אליפסה')}
          </button>
          <button type="button" className={`flow-editor-tool-btn ${mode === 'connect' ? 'active' : ''}`} onClick={() => { setMode('connect'); setConnectFrom(null); }}>
            {t('Arrow', 'חץ')}
          </button>
          <button type="button" className="flow-editor-tool-btn danger" onClick={deleteSelected} disabled={!selectedId}>
            {t('Delete', 'מחק')}
          </button>
          <button type="button" className="flow-editor-tool-btn danger" onClick={clearAll} disabled={nodes.length === 0}>
            {t('Clear all', 'נקה הכל')}
          </button>
          <div className="flow-editor-meta">
            <span>{t('Canvas', 'לוח')}</span>
            <select value={String(sizeIdx)} onChange={(e) => setSizeIdx(Number(e.target.value))} aria-label={t('Canvas size', 'גודל לוח')}>
              {SIZES.map((s, i) => (
                <option key={s.label} value={String(i)}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedNode && mode === 'select' && (
          <div className="flow-editor-selection-panel" role="region" aria-label={t('Selected object', 'אובייקט נבחר')}>
            <label className="flow-editor-selection-label">
              {t('Label', 'תווית')}
              <input
                type="text"
                className="flow-editor-selection-input"
                value={selectedNode.text || ''}
                onChange={(e) => updateSelectedText(e.target.value)}
                maxLength={120}
              />
            </label>
            <button type="button" className="flow-editor-tool-btn" onClick={duplicateSelected}>
              {t('Duplicate', 'שכפל')}
            </button>
          </div>
        )}

        <div className="flow-editor-body">
          <div className="flow-editor-canvas-wrap">
            <canvas
              ref={canvasRef}
              className={`flow-editor-canvas ${mode === 'select' ? 'flow-editor-canvas--select' : ''}`}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
              onDoubleClick={onDoubleClick}
            />
          </div>
          <p className="flow-editor-hint">{hintText}</p>
        </div>

        <div className="flow-editor-actions">
          <button type="button" onClick={onClose}>
            {t('Cancel', 'ביטול')}
          </button>
          <button
            type="button"
            className="flow-editor-actions-secondary"
            onClick={() => applyToPage(true)}
            disabled={!selectedNode}
            title={t('Add only the selected shape (and its arrows) to the page', 'הוספת הצורה הנבחרת (והחיצים אליה/ממנה) לדף')}
          >
            {t('Use selected object', 'הוסף אובייקט נבחר')}
          </button>
          <button type="button" className="primary" onClick={() => applyToPage(false)} disabled={nodes.length === 0}>
            {t('Use full diagram', 'הפוך לדף צביעה (כל התרשים)')}
          </button>
        </div>
      </div>
    </div>
  )
}
